import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { setupAPIclient } from "../../services/setupAPIclient";
import LocalStorage from "../../helpers/localstorage";
import Swal from "sweetalert2";
import {
  FiArrowLeft,
  FiEye,
  FiEyeOff,
  FiKey,
  FiLock,
  FiLogIn,
  FiMail,
  FiShield,
  FiUser,
} from "react-icons/fi";
import { motion } from "framer-motion";
import ThemeToggle from "../../components/common/ThemeToggle";

type ActiveForm = "login" | "register" | "resetEmail" | "resetCode" | "resetPassword";

const colors = {
  lightGray: "#F5F6FA",
  darkGray: "#7A7A7A",
  red: "rgb(255, 125, 112)",
};

interface FormDataState {
  name: string;
  email: string;
  password: string;
  number_phone: string;
  city: string;
}

interface BaseFormProps {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void> | void;
}

function AuthFormShell({ title, subtitle, children, onSubmit }: BaseFormProps) {
  return (
    <motion.form
      onSubmit={onSubmit}
      className="d-flex flex-column gap-3"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: "easeOut" }}
    >
      <h2 className="modern-auth-title h4 fw-bold mb-1">{title}</h2>
      <p className="modern-auth-subtitle">{subtitle}</p>
      {children}
    </motion.form>
  );
}

function Field({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <label className="modern-field">
      <span style={{ color: "rgb(93, 173, 226)" }}>{icon}</span>
      {children}
    </label>
  );
}

function PasswordField({
  value,
  onChange,
  placeholder,
  name,
  visible,
  onToggle,
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  name?: string;
  visible: boolean;
  onToggle: () => void;
}) {
  return (
    <Field icon={<FiLock />}>
      <input
        type={visible ? "text" : "password"}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
      <button type="button" className="modern-password-toggle" onClick={onToggle}>
        {visible ? <FiEyeOff /> : <FiEye />}
        <span>{visible ? "Ocultar" : "Mostrar"}</span>
      </button>
    </Field>
  );
}

export default function AuthenticationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeForm, setActiveForm] = useState<ActiveForm>("login");
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState<FormDataState>({
    name: "",
    email: "",
    password: "",
    number_phone: "",
    city: "",
  });

  const [code, setCode] = useState("");
  const [token, setToken] = useState("");

  React.useEffect(() => {
    const mode = searchParams.get("mode");
    if (mode === "register") {
      setActiveForm("register");
    }
  }, [searchParams]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  async function handleLogin(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const api = setupAPIclient();
    const res = await api.post("/auth/login", { email: formData.email, password: formData.password });
    if (res.data.error) return toast.error("Credenciais inválidas");
    new LocalStorage().saveAuthToken(res.data.token);
    navigate("/HomePage");
  }

  async function handleRegister(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const fields = Object.entries(formData);
    const emptyFields = fields.filter(([_, value]) => !value || String(value).trim() === "");

    if (emptyFields.length > 0) {
      await Swal.fire({
        icon: "warning",
        title: "Campos incompletos",
        text: "Preencha todos os campos obrigatórios.",
        confirmButtonText: "Ok",
        background: colors.lightGray,
        color: colors.darkGray,
        confirmButtonColor: colors.red,
      });
      return;
    }

    if (!formData.password || formData.password.length < 6) {
      await Swal.fire({
        icon: "warning",
        title: "Senha fraca",
        text: "A senha deve ter no mínimo 6 caracteres.",
        confirmButtonText: "Ok",
        background: colors.lightGray,
        color: colors.darkGray,
        confirmButtonColor: colors.red,
      });
      return;
    }

    const api = setupAPIclient();

    Swal.fire({
      title: "Criando cadastro...",
      text: "Aguarde um momento",
      allowOutsideClick: false,
      allowEscapeKey: false,
      background: colors.lightGray,
      color: colors.darkGray,
      didOpen: () => {
        Swal.showLoading();
        const loader = Swal.getPopup()?.querySelector(".swal2-loader");
        if (loader) {
          (loader as HTMLElement).style.borderTopColor = colors.darkGray;
        }
      },
    });

    try {
      const response = await api.post("/auth/register", formData);
      const { error, message, has_data } = response.data;

      Swal.close();

      if (has_data === false) {
        await Swal.fire({
          icon: "warning",
          title: "Cadastro já existente",
          text: "Este e-mail já possui cadastro.",
          confirmButtonText: "Entendi",
          background: colors.lightGray,
          color: colors.darkGray,
          confirmButtonColor: colors.red,
        });
        return;
      }

      if (error) {
        await Swal.fire({
          icon: "error",
          title: "Erro no cadastro",
          text: message || "Não foi possível criar o usuário.",
          confirmButtonText: "Entendi",
          background: colors.lightGray,
          color: colors.darkGray,
          confirmButtonColor: colors.red,
        });
        return;
      }

      await Swal.fire({
        icon: "success",
        title: "Cadastro realizado",
        text: message || "Usuário criado com sucesso!",
        confirmButtonText: "OK",
        background: colors.lightGray,
        color: colors.darkGray,
        confirmButtonColor: colors.red,
      });

      setActiveForm("login");
    } catch (err) {
      Swal.close();

      await Swal.fire({
        icon: "error",
        title: "Erro inesperado",
        text: "Ocorreu um erro ao tentar criar o usuário. Tente novamente.",
        confirmButtonText: "Fechar",
        background: colors.lightGray,
        color: colors.darkGray,
        confirmButtonColor: colors.red,
      });

      console.error("Erro ao registrar usuário:", err);
    }
  }

  async function handleVerifyEmail(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const api = setupAPIclient();
    const res = await api.post("/auth/verify_email", { email: formData.email });
    if (res.data.login_error) return toast.error("E-mail não encontrado");
    setToken(res.data.token);
    setActiveForm("resetCode");
  }

  async function handleVerifyCode(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const api = setupAPIclient();
    const res = await api.post("/auth/verify_cod", { code, token });
    if (!res.data.success) return toast.error("Código inválido");
    setActiveForm("resetPassword");
  }

  async function handleResetPassword(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const api = setupAPIclient(token);
    await api.post("/auth/resetpassword", { code, password: formData.password });
    toast.success("Senha atualizada!");
    setActiveForm("login");
  }

  return (
    <div className="modern-auth-page">
      <section className="modern-auth-left d-flex flex-column justify-content-center">
        <div className="modern-auth-brand-copy">
          <div className="modern-auth-badge">
            <FiShield />
            Plataforma de gestão contratual
          </div>
          <h1 className="modern-auth-hero-title">Gestão de Contratos BIM</h1>
          <p className="modern-auth-hero-text">
            Controle completo dos seus contratos, signatários e documentos com fluxo seguro, organizado e profissional.
          </p>
          <div className="modern-auth-meta">
            <span>Desenvolvimento em TI LTDA - ME</span>
            <span>CNPJ: 30.937.045/0001-58</span>
            <span>byteinmotion.com.br</span>
          </div>
        </div>
        <div className="auth-docs-3d" aria-hidden="true">
          <div className="auth-doc auth-doc-one">
            <span />
            <span />
            <span className="short" />
          </div>
          <div className="auth-doc auth-doc-two">
            <span />
            <span />
            <span className="short" />
          </div>
          <div className="auth-doc auth-doc-three">
            <span />
            <span />
            <span className="short" />
          </div>
          <div className="auth-doc-seal">OK</div>
        </div>
      </section>

      <section className="modern-auth-right">
        <div className="modern-auth-panel">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Link to="/" className="modern-helper-link text-decoration-none">Voltar</Link>
            <ThemeToggle />
          </div>
          {activeForm === "login" && (
            <AuthFormShell title="Acessar conta" subtitle="Entre com suas credenciais para continuar" onSubmit={handleLogin}>
              <Field icon={<FiMail />}>
                <input
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="E-mail"
                />
              </Field>

              <PasswordField
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Senha"
                visible={showPassword}
                onToggle={() => setShowPassword((prev) => !prev)}
              />

              <button type="submit" className="btn modern-primary-btn py-2 fw-semibold mt-2">
                <FiLogIn className="me-2" />
                Entrar
              </button>

              <button type="button" className="btn btn-link modern-helper-link p-0 text-start" onClick={() => setActiveForm("resetEmail")}>
                <FiKey className="me-1" /> Esqueci minha senha
              </button>

              <button type="button" className="btn btn-link modern-helper-link p-0 text-start" onClick={() => setActiveForm("register")}>
                <FiUser className="me-1" /> Criar conta
              </button>

              <Link to="/AuthUserToSubmasterPage" className="modern-helper-link text-decoration-none fw-semibold">
                Entrar como signatário
              </Link>
            </AuthFormShell>
          )}

          {activeForm === "register" && (
            <AuthFormShell title="Criar conta" subtitle="Cadastre sua organização para iniciar" onSubmit={handleRegister}>
              <Field icon={<FiUser />}>
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Nome"
                />
              </Field>

              <Field icon={<FiMail />}>
                <input
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="E-mail"
                />
              </Field>

              <label className="modern-field" style={{ padding: "0.4rem 0.5rem" }}>
                <PhoneInput
                  country="br"
                  value={formData.number_phone}
                  onChange={(value) => setFormData((prev) => ({ ...prev, number_phone: value }))}
                  inputStyle={{
                    border: "none",
                    width: "100%",
                    background: "transparent",
                    boxShadow: "none",
                  }}
                  buttonStyle={{ border: "none", background: "transparent" }}
                />
              </label>

              <Field icon={<FiShield />}>
                <select name="city" value={formData.city} onChange={handleChange}>
                  <option value="">Escolher país</option>
                  <option>Brasil</option>
                  <option>Portugal</option>
                  <option>Estados Unidos</option>
                </select>
              </Field>

              <PasswordField
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Senha"
                visible={showPassword}
                onToggle={() => setShowPassword((prev) => !prev)}
              />

              <button type="submit" className="btn modern-primary-btn py-2 fw-semibold mt-2">Cadastrar</button>
              <button type="button" className="btn btn-link modern-helper-link p-0 text-start" onClick={() => setActiveForm("login")}>
                <FiArrowLeft className="me-1" /> Voltar ao login
              </button>
            </AuthFormShell>
          )}

          {activeForm === "resetEmail" && (
            <AuthFormShell title="Recuperar senha" subtitle="Informe o e-mail da sua conta" onSubmit={handleVerifyEmail}>
              <Field icon={<FiMail />}>
                <input
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="E-mail"
                />
              </Field>

              <button type="submit" className="btn modern-primary-btn py-2 fw-semibold mt-2">Validar e-mail</button>
              <button type="button" className="btn btn-link modern-helper-link p-0 text-start" onClick={() => setActiveForm("login")}>
                <FiArrowLeft className="me-1" /> Voltar ao login
              </button>
            </AuthFormShell>
          )}

          {activeForm === "resetCode" && (
            <AuthFormShell title="Validar código" subtitle="Digite o código enviado para seu e-mail" onSubmit={handleVerifyCode}>
              <Field icon={<FiKey />}>
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Código"
                />
              </Field>

              <button type="submit" className="btn modern-primary-btn py-2 fw-semibold mt-2">Validar código</button>
            </AuthFormShell>
          )}

          {activeForm === "resetPassword" && (
            <AuthFormShell title="Nova senha" subtitle="Defina uma nova senha para acesso" onSubmit={handleResetPassword}>
              <PasswordField
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Nova senha"
                visible={showPassword}
                onToggle={() => setShowPassword((prev) => !prev)}
              />

              <button type="submit" className="btn modern-primary-btn py-2 fw-semibold mt-2">Redefinir senha</button>
            </AuthFormShell>
          )}
        </div>
      </section>
    </div>
  );
}
